import React, { useState, useEffect } from 'react';
import { FiX, FiPlus, FiTrash2, FiEdit2, FiSave, FiAlertCircle } from 'react-icons/fi';
import { getCommissionRules, addCommissionRule, updateCommissionRule, deleteCommissionRule } from '../../services/firestore';
import toast from 'react-hot-toast';

const RulesManagerModal = ({ isOpen, onClose, onRulesChange }) => {
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [newRule, setNewRule] = useState({ title: '', situation: '', result: '✅ Sí hay comisión', reason: '' });

    // Cargar reglas
    const fetchRules = async () => {
        setLoading(true);
        try {
            const data = await getCommissionRules();
            setRules(data);
            if (onRulesChange) onRulesChange(data);
        } catch (error) {
            console.error("Error loading rules:", error);
            toast.error("Error al cargar reglas");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) fetchRules();
    }, [isOpen]);

    const handleAdd = async () => {
        if (!newRule.title || !newRule.situation || !newRule.reason) {
            toast.error("Completa todos los campos");
            return;
        }
        try {
            await addCommissionRule(newRule);
            toast.success("Regla agregada");
            setNewRule({ title: '', situation: '', result: '✅ Sí hay comisión', reason: '' });
            fetchRules();
        } catch (error) {
            toast.error("Error al guardar");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("¿Estás seguro de eliminar esta regla?")) return;
        try {
            await deleteCommissionRule(id);
            toast.success("Regla eliminada");
            fetchRules();
        } catch (error) {
            toast.error("Error al eliminar");
        }
    };

    const handleEdit = (rule) => {
        setEditingId(rule.id);
        setNewRule({ ...rule });
    };

    const handleUpdate = async () => {
        try {
            const { id, ...data } = newRule;
            await updateCommissionRule(editingId, data);
            toast.success("Regla actualizada");
            setEditingId(null);
            setNewRule({ title: '', situation: '', result: '✅ Sí hay comisión', reason: '' });
            fetchRules();
        } catch (error) {
            toast.error("Error al actualizar");
        }
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setNewRule({ title: '', situation: '', result: '✅ Sí hay comisión', reason: '' });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col font-sans">
                {/* Header */}
                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50 rounded-t-2xl">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                            ⚙️ Gestión de Reglas
                            <span className="bg-yellow-100 text-yellow-700 text-xs px-2 py-1 rounded-full">{rules.length} Reglas</span>
                        </h3>
                        <p className="text-gray-500 text-sm">Define dinámicamente cómo la IA evalúa las comisiones</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                        <FiX size={24} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 bg-gray-50/30">

                    {/* Formulario */}
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-6">
                        <h4 className="font-bold text-gray-700 mb-3 flex items-center gap-2">
                            {editingId ? <><FiEdit2 /> Editando Regla</> : <><FiPlus /> Nueva Regla</>}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <input
                                placeholder="Título (ej. Caso 1: Cliente decidido)"
                                className="border p-2 rounded-lg w-full"
                                value={newRule.title}
                                onChange={e => setNewRule({ ...newRule, title: e.target.value })}
                            />
                            <select
                                className="border p-2 rounded-lg w-full font-medium"
                                value={newRule.result}
                                onChange={e => setNewRule({ ...newRule, result: e.target.value })}
                            >
                                <option value="✅ Sí hay comisión">✅ Sí hay comisión</option>
                                <option value="❌ No hay comisión">❌ No hay comisión</option>
                            </select>
                            <textarea
                                placeholder="Situación (Descripción detallada)"
                                className="border p-2 rounded-lg w-full h-20"
                                value={newRule.situation}
                                onChange={e => setNewRule({ ...newRule, situation: e.target.value })}
                            />
                            <textarea
                                placeholder="Motivo / Razón"
                                className="border p-2 rounded-lg w-full h-20"
                                value={newRule.reason}
                                onChange={e => setNewRule({ ...newRule, reason: e.target.value })}
                            />
                        </div>
                        <div className="flex justify-end gap-2 mt-4">
                            {editingId && (
                                <button onClick={handleCancelEdit} className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded-lg">
                                    Cancelar
                                </button>
                            )}
                            <button
                                onClick={editingId ? handleUpdate : handleAdd}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-bold shadow-md transition-colors"
                            >
                                {editingId ? 'Actualizar Regla' : 'Agregar Regla'}
                            </button>
                        </div>
                    </div>

                    {/* Lista */}
                    {loading ? (
                        <div className="text-center py-10"><span className="animate-spin text-3xl">🌀</span></div>
                    ) : (
                        <div className="grid gap-3">
                            {rules.map((rule) => (
                                <div key={rule.id} className="bg-white p-4 rounded-xl border border-gray-200 hover:shadow-md transition-shadow flex flex-col md:flex-row gap-4 items-start md:items-center group">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="font-bold text-gray-800">{rule.title}</span>
                                            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${rule.result.includes('Sí') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                {rule.result}
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-600 mb-1"><span className="font-semibold text-xs uppercase text-gray-400">Situación:</span> {rule.situation}</p>
                                        <p className="text-sm text-gray-500"><span className="font-semibold text-xs uppercase text-gray-400">Motivo:</span> {rule.reason}</p>
                                    </div>
                                    <div className="flex gap-2 opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity self-end md:self-center">
                                        <button onClick={() => handleEdit(rule)} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg">
                                            <FiEdit2 />
                                        </button>
                                        <button onClick={() => handleDelete(rule.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                                            <FiTrash2 />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RulesManagerModal;
